"use client";

import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { SectionHeader } from "@/components/ui/section-header";
import { evolutionStages } from "@/lib/data/site";
import { cn } from "@/lib/utils";

export function AiPositioning() {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const lineWidth = useTransform(scrollYProgress, [0.15, 0.75], ["0%", "100%"]);
  const glowX = useTransform(scrollYProgress, [0.2, 0.8], ["0%", "100%"]);

  return (
    <section ref={ref} className="relative overflow-hidden py-20 sm:py-24 md:py-32">
      {/* Ambient atmosphere */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_20%_0%,var(--hero-glow-1),transparent_55%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_85%_60%,var(--hero-glow-2),transparent_50%)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border-strong to-transparent" />

      <div className="container-wide relative">
        <div className="grid gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:items-end lg:gap-16">
          <SectionHeader
            eyebrow="AI-First Philosophy"
            title={"AI ISN'T A FEATURE.\nIT'S A NEW BUSINESS FOUNDATION."}
            description="Enterprises are evolving from traditional software into agentic organizations — where intelligence is embedded into every decision, workflow, and customer moment."
          />

          <motion.aside
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="relative overflow-hidden rounded-[1.75rem] border border-border bg-card/60 p-6 backdrop-blur-xl sm:p-7"
          >
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-accent/10 via-transparent to-cyan/10" />
            <div className="pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full bg-accent/20 blur-2xl" />
            <p className="relative text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">The shift</p>
            <p className="relative mt-4 font-display text-2xl tracking-tight text-foreground sm:text-3xl">
              From tools that <span className="text-muted">record work</span>
              <br />
              to systems that <span className="gradient-text">do the work.</span>
            </p>
            <div className="relative mt-6 flex items-center gap-3 border-t border-border pt-5">
              <span className="h-1.5 w-1.5 rounded-full bg-cyan shadow-[0_0_12px_var(--accent-glow)]" />
              <p className="text-sm text-muted">Five stages. One destination: agentic enterprise.</p>
            </div>
          </motion.aside>
        </div>

        {/* Evolution journey */}
        <div className="relative mt-16 sm:mt-20">
          <div className="mb-8 flex items-end justify-between gap-4 sm:mb-10">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted">Enterprise evolution</p>
              <p className="mt-2 max-w-md text-sm text-muted">
                A clear path from legacy software to intelligence that acts.
              </p>
            </div>
            <p className="hidden font-mono text-xs text-accent sm:block">01 → 05</p>
          </div>

          {/* Progress rail */}
          <div className="relative mb-2 hidden h-px bg-border md:block">
            <motion.div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-accent via-violet to-cyan"
              style={{ width: lineWidth }}
            />
            <motion.div
              className="absolute top-1/2 h-3 w-3 -translate-y-1/2 rounded-full bg-cyan shadow-[0_0_20px_var(--accent-glow)]"
              style={{ left: glowX }}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-5 md:gap-3 lg:gap-4">
            {evolutionStages.map((stage, index) => {
              const isLast = index === evolutionStages.length - 1;
              return (
                <motion.article
                  key={stage.title}
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ delay: index * 0.08, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -6 }}
                  className={cn(
                    "group relative overflow-hidden rounded-[1.5rem] border p-5 transition duration-500 sm:p-6",
                    isLast
                      ? "border-accent/40 bg-gradient-to-br from-accent/15 via-card to-cyan/10 shadow-[var(--shadow-glow)]"
                      : "border-border bg-card/50 hover:border-border-strong hover:bg-card/80",
                  )}
                >
                  <div className="pointer-events-none absolute inset-0 opacity-0 transition duration-500 group-hover:opacity-100">
                    <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-accent/15 blur-2xl" />
                  </div>

                  {/* Node marker */}
                  <div className="relative mb-5 flex items-center justify-between">
                    <div
                      className={cn(
                        "relative flex h-9 w-9 items-center justify-center rounded-full border text-xs font-mono",
                        isLast
                          ? "border-cyan bg-cyan/15 text-cyan shadow-[0_0_24px_var(--accent-glow)]"
                          : "border-accent/40 bg-background text-accent",
                      )}
                    >
                      0{index + 1}
                      {isLast ? (
                        <span className="absolute inset-0 animate-ping rounded-full bg-cyan/30" />
                      ) : null}
                    </div>
                    {!isLast ? (
                      <span className="hidden text-muted md:inline" aria-hidden>
                        →
                      </span>
                    ) : (
                      <span className="rounded-full border border-cyan/30 bg-cyan/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-cyan">
                        Now
                      </span>
                    )}
                  </div>

                  <h3 className="relative font-display text-xl tracking-tight text-foreground lg:text-2xl">
                    {stage.title}
                  </h3>
                  <p className="relative mt-3 text-sm leading-relaxed text-muted">{stage.summary}</p>

                  <div
                    className={cn(
                      "relative mt-6 h-0.5 w-10 origin-left rounded-full bg-gradient-to-r from-accent to-cyan transition duration-500",
                      isLast ? "scale-x-100" : "scale-x-50 group-hover:scale-x-100",
                    )}
                  />
                </motion.article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
